/**
 * Enhanced GA Tracking – Context and hook
 * Provides tracking functions to components. No-op when enhancedTracking.enabled is false.
 */

import React, {createContext, useContext, useEffect} from 'react';
import * as track from './tracking';

const TrackingContext = createContext(null);

/**
 * Provider that initializes tracking when settings are available.
 * Wrap app/routes with this when settings are loaded.
 */
export const TrackingProvider = ({settings, children}) => {
  useEffect(() => {
    if (settings) {
      track.init(settings);
    }
  }, [settings]);

  const enabled = Boolean(settings?.enhancedTracking?.enabled);
  const value = enabled
    ? {
      trackResultClick: track.resultClick,
      trackSimpleBrochureClick: track.simpleBrochureClick,
      trackRequestRepClick: track.requestRepClick,
      trackExternalLinkExitModal: track.externalLinkExitModal,
      filtersApply: track.filtersApply,
      buildFiltersApplyParams: track.buildFiltersApplyParams,
      shouldTrack: track.shouldTrack
    }
    : {
      trackResultClick: () => {},
      trackSimpleBrochureClick: () => {},
      trackRequestRepClick: () => {},
      trackExternalLinkExitModal: () => {},
      filtersApply: () => {},
      buildFiltersApplyParams: () => ({}),
      shouldTrack: () => false
    };

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
}

/**
 * Hook to access tracking functions.
 * Returns no-op functions when enhanced tracking is disabled.
 */
export const useTracking = () => {
  const context = useContext(TrackingContext);
  if (!context) {
    return {
      trackResultClick: () => {},
      trackSimpleBrochureClick: () => {},
      trackRequestRepClick: () => {},
      trackExternalLinkExitModal: () => {},
      filtersApply: () => {},
      buildFiltersApplyParams: () => ({}),
      shouldTrack: () => false
    };
  }
  return context;
};
